import {
  routineFocusAreaIds,
  routinePurposeDefinitions,
  type RoutineCategoryId,
  type RoutineFocusAreaId,
  type RoutinePurposeId,
} from '@forma/domain';
import { getRoutineFocusAreaLabel, getRoutinePurposeLabel } from '@forma/i18n';
import type { Translator } from './i18n';

export {
  defaultRoutinePurposeId,
  getRoutinePurposeIcon,
  getRoutinePurposeLegacyCategoryId,
  isRoutinePurposeId,
  normalizeRoutinePurposeId,
  routineFocusAreaIds,
  routinePurposeDefinitions,
  routinePurposeIds,
  type RoutineCategoryId,
  type RoutineFocusAreaId,
  type RoutinePurposeId,
} from '@forma/domain';

export type RoutineCategoryOption = {
  id: RoutineCategoryId;
  icon: string;
  label: string;
};

export type RoutinePurposeOption = {
  id: RoutinePurposeId;
  label: string;
};

export type RoutineFilterOption<TId extends string = string> = {
  id: TId;
  label: string;
};

export const defaultRoutineCategoryId: RoutineCategoryId = 'general';
export const routineCategoryDefinitions = [
  { id: 'general', icon: 'sparkles', label: 'General' },
  { id: 'football', icon: 'volleyball', label: 'Football' },
  { id: 'recovery', icon: 'snowflake', label: 'Recovery' },
  { id: 'mobility', icon: 'stretch-horizontal', label: 'Mobility' },
  { id: 'strength', icon: 'biceps-flexed', label: 'Strength' },
  { id: 'prehab', icon: 'shield', label: 'Prehab' },
  { id: 'cardio', icon: 'footprints', label: 'Cardio' },
  { id: 'conditioning', icon: 'heart-pulse', label: 'Conditioning' },
  { id: 'core', icon: 'circle-dot', label: 'Core' },
  { id: 'legs', icon: 'footprints', label: 'Legs' },
  { id: 'intensity', icon: 'flame', label: 'Intensity' },
] as const satisfies readonly RoutineCategoryOption[];

export const routineCategoryIds = routineCategoryDefinitions.map(
  (category) => category.id,
);
export const routineIntentIds = [
  'warmup',
  'cooldown',
  'recovery',
  'prehab',
  'strength',
  'mobility',
  'conditioning',
  'relaxation',
];
export const routineTagDefinitions = [
  { id: 'football', label: 'Football' },
  { id: 'running', label: 'Running' },
  { id: 'desk', label: 'Desk' },
  { id: 'gym', label: 'Gym' },
  { id: 'home', label: 'Home' },
  { id: 'no_equipment', label: 'No equipment' },
] as const satisfies readonly RoutineFilterOption[];

export function isRoutineCategoryId(
  value: unknown,
): value is RoutineCategoryId {
  return (
    typeof value === 'string' &&
    routineCategoryIds.includes(value as RoutineCategoryId)
  );
}

export function normalizeRoutineCategoryId(value: unknown): RoutineCategoryId {
  return isRoutineCategoryId(value) ? value : defaultRoutineCategoryId;
}

export function getRoutineCategoryIcon(categoryId: RoutineCategoryId) {
  return (
    routineCategoryDefinitions.find((category) => category.id === categoryId)
      ?.icon ?? '✨'
  );
}

export function getRoutineCategoryOptions(
  t: Translator,
): RoutineCategoryOption[] {
  return routineCategoryDefinitions.map((category) => ({
    ...category,
    label: t(category.label),
  }));
}

export function getRoutinePurposeOptions(
  t: Translator,
): RoutinePurposeOption[] {
  return routinePurposeDefinitions.map(({ id }) => ({
    id,
    label: t.language
      ? getRoutinePurposeLabel(t.language, id)
      : t(getRoutinePurposeLabel('en', id)),
  }));
}

export function getRoutineFocusAreaOptions(
  t: Translator,
): RoutineFilterOption<RoutineFocusAreaId>[] {
  return routineFocusAreaIds.map((id) => ({
    id,
    label: t.language
      ? getRoutineFocusAreaLabel(t.language, id)
      : t(getRoutineFocusAreaLabel('en', id)),
  }));
}

export function getRoutineTagOptions(t: Translator): RoutineFilterOption[] {
  return routineTagDefinitions.map((tag) => ({
    ...tag,
    label: t(tag.label),
  }));
}

export function getRoutineIntentOptions(t: Translator): RoutineFilterOption[] {
  return [
    { id: 'warmup', label: t('Warmup') },
    { id: 'cooldown', label: t('Cooldown') },
    { id: 'recovery', label: t('Recovery') },
    { id: 'prehab', label: t('Prehab') },
    { id: 'strength', label: t('Strength') },
    { id: 'mobility', label: t('Mobility') },
    { id: 'conditioning', label: t('Conditioning') },
  ];
}

export function getReminderDayOptions(t: Translator) {
  return [
    { key: 'mon', label: t('Mon') },
    { key: 'tue', label: t('Tue') },
    { key: 'wed', label: t('Wed') },
    { key: 'thu', label: t('Thu') },
    { key: 'fri', label: t('Fri') },
    { key: 'sat', label: t('Sat') },
    { key: 'sun', label: t('Sun') },
  ] as const;
}

export function getSetupSteps(t: Translator) {
  return [
    t('Create a Firebase web app in the Firebase console.'),
    t('Enable Google in Authentication > Sign-in method.'),
    t('Enable Anonymous in Authentication > Sign-in method.'),
    t('Enable Firestore Database in the Firebase console.'),
    t('Add the Firebase web config to apps/web/.env.local.'),
    t('Restart the Vite dev server after updating env vars.'),
  ];
}
